import React, { useEffect, useState } from 'react'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'
import { useIntl } from 'react-intl'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { FilterFn, ColumnFiltersState, ColumnDef, useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table'
import { Table } from 'react-bootstrap'
import TagManager from 'react-gtm-module'
import { useSearchParams } from 'react-router-dom'
import { getPublicSelectedFiles } from '../core/_requests'
import { getFileType } from '../../../core/_util'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useTimeZone } from '../../../hooks/useTimeZone'
import { toast } from 'react-toastify'
import { NoDataPage } from '../../resources/components/NoDataPage'
import ViewDocModal from '../../stakeholders/stakeholders-and-investors/components/individual/view-doc-modal'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}

const QueryPromptTable = () => {
    const { formatMessage } = useIntl()
    const { getDateValue } = useDateFormat()
    const [searchParams] = useSearchParams()
    const { getTimeZoneValue } = useTimeZone()
    const fileVersionIds = searchParams.get('fileVersionIds')
    const companyId = searchParams.get('companyId')
    const [shareholders, setShareholders] = useState<any>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isActive, setIsActive] = useState<boolean>(false)
    const [viewData, setViewData] = useState<any>({})
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    })

    useEffect(() => {
        const fetchData = async () => {
            if (!fileVersionIds || !companyId) return
            setIsLoading(true)
            try {
                const { data: { data, success } } = await getPublicSelectedFiles(fileVersionIds, companyId)
                if (success)
                    setShareholders(data?.downloadDetails)
            } catch (error) {
                console.error('Error fetching data:', error)
            }
            finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [])// eslint-disable-line

    const columns = React.useMemo<ColumnDef<any, any>[]>(() => [
        {
            accessorKey: 'fileName',
            id: 'fileName',
            header: 'File Name',
            cell: (info) => (
                <div className='d-flex align-items-center gap-2 pe-4 '>
                    <img
                        src={getFileType(info.row.original.fileName)}
                        alt="pdf"
                        height={32}
                        width={32}
                    />
                    <div>
                        <h5 className='m-0 fw-normal font-size-13'>{info.row.original.documentName}</h5>
                    </div>
                </div>
            ),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'createdAt',
            id: 'createdAt',
            header: 'Date & Time',
            cell: (info) => `${getDateValue(info.getValue())} ${getTimeZoneValue(info.getValue())}`,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'createdBy',
            id: 'createdBy',
            header: 'Created By',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
    ], [])// eslint-disable-line
    const onDownloadFile = async (link: any) => {
        try {
            const a = document.createElement('a')
            a.href = link
            a.download = 'download'
            document.body.appendChild(a)
            a.click()
            a.remove() // This line is sufficient to remove the element from the DOM
            toast.success(formatMessage({ id: 'File downloaded successfully' }))
        }
        catch (error) {
            console.error(error)
        }
    }
    const table = useReactTable({
        data: shareholders,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnFilters,
            globalFilter,
            pagination,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        globalFilterFn: 'fuzzy',
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })
    console.log(!isLoading && (shareholders?.length === 0 || table.getRowModel().rows.length === 0), shareholders, isLoading);

    return (
        <>
            {/* <div className='founder-create-logo d-flex justify-content-center my-4'>
                <img
                    src={toAbsoluteUrl(brandingConfig.lightModeLogo)}
                    alt='Logo'
                    height={45}
                    width={auto}
                />
            </div> */}
            <div className='p-8 bg-white rounded mb-4 d-flex flex-column gap-4 h-100'>
                <InfoCard2
                    title={formatMessage({
                        // id: 'Submit & Send Files Easily with RecordsKeeper',
                        id: 'Access Shared Files Instantly with RecordsKeeper.AI',
                    })}
                    desc={formatMessage({
                        // id: `Submit the requested files quickly and easily through this page. Just upload your files and click 'Submit'. The request creator will be automatically notified once your submission is complete.`,
                        id: `You've received the following file(s) via RecordsKeeper.AI. Simply click View or Download to access them seamlessly. If you encounter any issues, feel free to contact the sender or reach out to our support team at support@help.recordskeeper.ai.`,
                    })}
                    desc2={formatMessage({ id: 'Experience secure, trackable, and efficient file sharing — powered by AI & Blockchain.' })}
                    slug={`https://help.recordskeeper.ai/en/articles/10867036-file-sharing-made-simple-how-to-submit-and-send-files-with-recordskeeper-ai`}
                    className='w-100 mb-0'
                />

                {/* <p className='font-size-12'>{formatMessage({ id: `Files Shared with You by {username}.` })}</p> */}

                <div>
                    <>
                        <div className='d-flex flex-md-row flex-column gap-2 mb-4'>
                            <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start w-100 gap-1'>
                                <h1 className='font-size-16 mb-0'>{formatMessage({ id: 'File Access' })}</h1>
                                <form className='align-items-center w-100 w-md-auto d-flex border border-2  rounded'>
                                    <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                                        <svg
                                            width='24'
                                            height='24'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                            className='mh-50px'
                                        >
                                            <rect
                                                opacity='0.5'
                                                x='17.0365'
                                                y='15.1223'
                                                width='8.15546'
                                                height='2'
                                                rx='1'
                                                transform='rotate(45 17.0365 15.1223)'
                                                fill='currentColor'
                                            ></rect>
                                            <path
                                                d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                                                fill='currentColor'
                                            ></path>
                                        </svg>
                                    </span>
                                    <DebouncedInput
                                        value={globalFilter ?? ''}
                                        onChange={(value) => {
                                            setGlobalFilter(String(value))
                                            if (String(value).length > 0) {
                                                TagManager.dataLayer({
                                                    dataLayer: {
                                                        // event: GTMEvent.AdvisorMentorSearchSuccessful,
                                                        action: 'normal',
                                                        // metadata: {
                                                        //     company_id: companyId,
                                                        //     user_id: currentUser?.userId,
                                                        //     email: currentUser?.email,
                                                        //     search_value: value,
                                                        // },
                                                    },
                                                })
                                            }
                                        }}
                                        className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
                                        placeholder='Search Files'
                                    />
                                </form>
                            </div>
                        </div>
                        <div className='bg-transparent rounded-top table-query-prompt' style={{ overflowX: 'auto' }}>
                            {!isLoading && (shareholders?.length === 0 || table.getRowModel().rows.length === 0) ? (
                                <div className='my-5 mx-6'>
                                    <NoDataPage />
                                </div>
                            )
                                :
                                <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
                                    <thead className='custom-thead border-bottom'>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <tr key={headerGroup.id} className='font-size-12 font-weight-400 table-row border-dark-mode'>
                                                {headerGroup.headers.map((header) => (
                                                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-0 '>
                                                        {header.isPlaceholder ? null : (
                                                            <div
                                                                className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                                {{
                                                                    asc: ' ▲',
                                                                    desc: ' ▼',
                                                                }[header.column.getIsSorted() as string] ?? null}
                                                            </div>
                                                        )}
                                                    </th>
                                                ))}
                                                <th className='min-w-120px pb-6 text-end '>{formatMessage({ id: 'Actions' })}</th>
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {
                                            isLoading ? (
                                                <>
                                                    <SkeletonRow count={8} />
                                                </>
                                            ) : (
                                                table.getRowModel().rows.map((row, index) => (
                                                    <React.Fragment key={row.id}>
                                                        <tr
                                                            key={row.id}
                                                            className={`border-bottom`}
                                                        >
                                                            {row.getVisibleCells().map((cell, cellIndex) => (
                                                                <>
                                                                    {cellIndex === 0 ? (
                                                                        <td key={cell.id} className='pt-6 pe-4 border-0' style={{ minWidth: '120px' }}>
                                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                        </td>
                                                                    ) : (
                                                                        <td key={cell.id} className='pt-7 px-4 border-0' style={{ minWidth: '120px' }}>
                                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                        </td>
                                                                    )}
                                                                </>
                                                            ))}
                                                            <td className='pt-5 ps-4' onClick={(e) => e.stopPropagation()}>
                                                                <div className='d-flex justify-content-end align-items-center pb-1 gap-2'>
                                                                    <button className='custom-button-property btn bg-light text-muted' onClick={() => {
                                                                        setViewData(row.original)
                                                                        setIsActive(true)
                                                                    }}>{formatMessage({ id: 'View' })}</button>
                                                                    <button className='custom-button-property btn bg-primary text-white' onClick={() => onDownloadFile(row.original.downloadUrl)}>{formatMessage({ id: 'Download' })}</button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                )))}
                                    </tbody>
                                </Table >
                            }
                        </div >
                        {/* <div className='w-100 d-md-flex justify-content-between align-items-center px-0 py-4 bg-transparent rounded-bottom mt-4'>
                            <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
                                <select
                                    aria-label='Default select example'
                                    className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-transparent text-dark '
                                    style={{ outline: 'none' }}
                                    value={pagination.pageSize}
                                    onChange={(e) => {
                                        const newSize = Number(e.target.value)
                                        setPagination((prev) => ({
                                            ...prev,
                                            pageSize: newSize,
                                            pageIndex: 0, // Reset to first page when page size changes
                                        }))
                                    }}
                                >
                                    {[10, 20, 50, 100].map((pageSize) => (
                                        <option key={pageSize} value={pageSize}>
                                            {pageSize}
                                        </option>
                                    ))}
                                </select>

                                <span className='font-size-13 text-center text-md-start'>
                                    {formatMessage({
                                        id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                                            (currentPage + 1) * pagination.pageSize,
                                            table.getPrePaginationRowModel().rows.length
                                        )} of ${table.getPrePaginationRowModel().rows.length} records`,
                                    })}
                                </span>
                            </div>

                            <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
                                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                                    {'<<'}
                                </Pagination.First>

                                <Pagination.Prev
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 0}
                                >
                                    {'<'}
                                </Pagination.Prev>

                                {Array.from({ length: pageCount }, (_, i) => (
                                    <Pagination.Item
                                        key={i}
                                        active={i === currentPage}
                                        onClick={() => handlePageChange(i)}
                                    >
                                        {i + 1}
                                    </Pagination.Item>
                                ))}

                                <Pagination.Next
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= pageCount - 1}
                                >
                                    {'>'}
                                </Pagination.Next>

                                <Pagination.Last
                                    onClick={() => handlePageChange(pageCount - 1)}
                                    disabled={currentPage >= pageCount - 1}
                                >
                                    {'>>'}
                                </Pagination.Last>
                            </Pagination>
                        </div> */}
                    </>
                </div>
            </div >
            {/* <div className='d-flex flex-md-row fw-semibold text-gray-500 fs-base justify-content-between w-100 d-flex flex-column align-items-center my-4'>
                <p
                    className='text-gray-500 m-md-0 mb-4'
                    dangerouslySetInnerHTML={{
                        __html: `&copy; ${currentYear} ${brandingConfig.companyDetails.displayName}`,
                    }}
                />
                <div className='d-md-flex align-items-center justify-content-center'>
                    <p className='mb-0 me-4 text-gray-500 text-center'>{formatMessage({ id: 'Powered By' })}</p>
                    <div className='founder-create-logo d-flex justify-content-center'>
                        <img
                            src={toAbsoluteUrl(brandingConfig.lightModeLogo)}
                            alt='Logo'
                            height={45}
                            width={auto}
                        />
                    </div>
                </div>
                <div className='d-flex'>
                    <a
                        href={brandingConfig.termsUrl}
                        className='me-4 text-gray-500'
                        target='_blank'
                        rel='noreferrer'
                    >
                        {formatMessage({ id: 'Terms of use' })}
                    </a>

                    <a
                        href={brandingConfig.privacyUrl}
                        className='text-gray-500'
                        target='_blank'
                        rel='noreferrer'
                    >
                        {formatMessage({ id: 'Privacy Policy' })}
                    </a>
                </div>
            </div> */}
            <ViewDocModal
                isActive={isActive}
                setIsActive={setIsActive}
                fileName={viewData.fileName}
                isRecordKeeper={true}
                isLoading={isLoading}
                viewableData={viewData}
            />
        </>
    )
}

export default QueryPromptTable
function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number;
    onChange: (value: string | number) => void;
    debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);

        return () => clearTimeout(timeout);
    }, [value, debounce, onChange]);
    return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />;
}
